/** Cinematic blend between two recital identities. */
export const ARTWORK_BACKDROP_TRANSITION_MS = 1000;

/**
 * Card surfaces read as settled sooner than a two-image crossfade at the same
 * numeric opacity, so their reveal gets a small perceptual compensation.
 */
export const CARD_BACKDROP_REVEAL_MS = 1250;

/** Clear visual atmosphere promptly when entering a neutral utility page. */
export const ARTWORK_TO_BLANK_BACKDROP_TRANSITION_MS = 120;

/** Restore atmosphere with a brief, softer landing when leaving a utility page. */
export const BLANK_TO_ARTWORK_BACKDROP_TRANSITION_MS = 220;

type CubicBezier = readonly [number, number, number, number];

export type BackdropTransitionMotion = {
  durationMs: number;
  easing: CubicBezier;
};

export type BackdropTransitionOrigin = {
  composed: boolean;
  uri: string | null;
};

export const ARTWORK_BACKDROP_MOTION: BackdropTransitionMotion = {
  durationMs: ARTWORK_BACKDROP_TRANSITION_MS,
  easing: [0.45, 0, 0.55, 1]
};

const ARTWORK_TO_BLANK_BACKDROP_MOTION: BackdropTransitionMotion = {
  durationMs: ARTWORK_TO_BLANK_BACKDROP_TRANSITION_MS,
  // Front-load the fade so black reads almost immediately without a hard cut.
  easing: [0.2, 0.8, 0.2, 1]
};

const BLANK_TO_ARTWORK_BACKDROP_MOTION: BackdropTransitionMotion = {
  durationMs: BLANK_TO_ARTWORK_BACKDROP_TRANSITION_MS,
  // A pronounced ease-out lets the atmosphere arrive quickly, then settle.
  easing: [0.16, 1, 0.3, 1]
};

const NO_BACKDROP_MOTION: BackdropTransitionMotion = {
  durationMs: 0,
  easing: [0, 0, 1, 1]
};

/**
 * Blank is a real route presentation, not an artwork identity. Entering or
 * leaving it should resolve decisively; only artwork-to-artwork changes use
 * the longer keepsake-style crossfade.
 */
export function getBackdropTransitionMotion(
  outgoingUri: string | null,
  incomingUri: string | null
): BackdropTransitionMotion {
  if (outgoingUri && incomingUri) return ARTWORK_BACKDROP_MOTION;
  if (outgoingUri) return ARTWORK_TO_BLANK_BACKDROP_MOTION;
  if (incomingUri) return BLANK_TO_ARTWORK_BACKDROP_MOTION;
  return NO_BACKDROP_MOTION;
}

/**
 * Web's body-level compositor survives React provider remounts and is
 * therefore authoritative whenever it has composed at least one route.
 * Native has no external compositor and keeps using provider-local state.
 */
export function resolveBackdropTransitionOrigin(
  provider: BackdropTransitionOrigin,
  persistent?: BackdropTransitionOrigin
): BackdropTransitionOrigin {
  return persistent?.composed ? persistent : provider;
}
